import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

export interface ChatInputContext {
  label: string
  message?: string
  placeholder?: string
  source?: string
  persistAfterSubmit?: boolean
}

interface UseDrawerAskAIComposerContextArgs {
  chatInputContext: ChatInputContext | undefined
  messageInputValue: string
  setChatInputContext: Dispatch<SetStateAction<ChatInputContext | undefined>>
  setMessageInputValue: Dispatch<SetStateAction<string>>
  focusChatInput: () => void
}

function isDrawerAskAIContext(context: ChatInputContext | undefined): context is ChatInputContext {
  return context?.persistAfterSubmit === true
}

export function useDrawerAskAIComposerContext({
  chatInputContext,
  messageInputValue,
  setChatInputContext,
  setMessageInputValue,
  focusChatInput,
}: UseDrawerAskAIComposerContextArgs) {
  const dismissedDrawerSourcesRef = useRef(new Set<string>())

  useEventSubscription('activate_drawer_ask_ai_context', (event) => {
    if (dismissedDrawerSourcesRef.current.has(event.source)) {
      return
    }

    if (chatInputContext && !isDrawerAskAIContext(chatInputContext)) {
      return
    }

    const isSameDrawerContext =
      chatInputContext?.source === event.source &&
      isDrawerAskAIContext(chatInputContext)

    setChatInputContext(current => {
      const isSameCurrentDrawer =
        current?.source === event.source &&
        isDrawerAskAIContext(current)
      if (isSameCurrentDrawer) return current

      return {
        label: event.label,
        // Capture the drawer context once when this drawer source activates.
        // Later same-drawer refreshes are intentionally ignored: another frontend
        // system owns page-change/update context, while this chip represents the
        // drawer the user started asking about.
        message: event.context,
        placeholder: event.placeholder,
        source: event.source,
        persistAfterSubmit: true,
      }
    })
    if (!isSameDrawerContext && messageInputValue) {
      setMessageInputValue('')
    }
    if (!isSameDrawerContext) {
      focusChatInput()
    }
  })

  useEventSubscription('clear_drawer_ask_ai_context', (event) => {
    dismissedDrawerSourcesRef.current.delete(event.source)
    setChatInputContext(current => current?.source === event.source ? undefined : current)
  })

  const clearChatInputContextManually = useCallback(
    () => {
      setChatInputContext(current => {
        if (isDrawerAskAIContext(current) && current.source) {
          dismissedDrawerSourcesRef.current.add(current.source)
        }

        return undefined
      })
    },
    [setChatInputContext],
  )

  const clearChatInputContextAfterSubmit = useCallback(
    (submittedContext: ChatInputContext) => {
      setChatInputContext(current => {
        if (current !== submittedContext) return current

        // Drawer context stays visible for follow-up questions, but the hidden
        // context payload is sent only once per drawer activation; after that the
        // agent already has that drawer context in the conversation.
        if (current.persistAfterSubmit) {
          return { ...current, message: undefined }
        }
        return undefined
      })
    },
    [setChatInputContext],
  )

  return { clearChatInputContextAfterSubmit, clearChatInputContextManually }
}
